import { SignInState } from "../app";
import * as Actions from "../store/actions";
import { NotificationInfo } from "../store/appState";
import { hasItems } from "./collections";

export type StringKeyValueMap = { [name: string]: string };

export function getAllCookies(): StringKeyValueMap {
    const result = {};
    // nikolami: We don't authenticate using cookies, we just store access token in them and then pass them in authorization header to frontend
    // so that they can be used as bearer tokens for calling web API from frontend. So we should be fine from CSRF prespective
    // eslint-disable-next-line @microsoft/sdl/no-cookies
    const rawCookies = document.cookie.split(";");
    rawCookies.forEach((cookie: string) => {
        if (cookie.trim() === "") {
            return;
        }
        let [name, value] = cookie.split('=');
        name = decodeURIComponent(name);
        value = decodeURIComponent(value);
        result[name.trim()] = value.trim();
    });

    return result;
}

export function setSameSiteCookie(name: string, value: string, expiryDays: number = 30): void {
    const date = new Date();
    date.setTime(date.getTime() + (expiryDays * 24 * 60 * 60 * 1000));
    // eslint-disable-next-line @microsoft/sdl/no-cookies
    document.cookie = `${name}=${encodeURIComponent(value)};SameSite=Strict;secure;expires=${date.toUTCString()}; path=/`;
}

export function deleteCookie(name: string): void {
    // eslint-disable-next-line @microsoft/sdl/no-cookies
    document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
}

// for security reasons restrict the variable names to the one caller expects
export function getVarsFromWindowUrl(varNames: string[]): StringKeyValueMap {
    return getVarsFromUrl(window.location.href, varNames);
}

export function getVarsFromUrl(url: string, varNames: string[]): StringKeyValueMap {
    const vars = {};

    if (!hasItems(varNames)) {
        return vars;
    }

    url.replace(/[?&]+([^=&]+)=([^&]*)/gi, (m: string, key: string, value: string) => {
        if (varNames.some(x => x === key)) {
            // eslint-disable-next-line security/detect-object-injection
            vars[key] = value;
        }
        return m;
    });
    return vars;
}

export const ensureProtocol = (url: string, protocol: string = "https"): string => {
    if (!url) {
        return url;
    }

    return url.match(/^https?:\/\//i) ? url : `${protocol}://${url}`;
}

export const ensureSignedIn = (signInState: SignInState, notificationKey: string, message: string): boolean => {
    if (signInState !== SignInState.Success) {
        Actions.setContextualNotification(notificationKey, {
            title: message,
            type: "information",
            isCritical: true
        } as NotificationInfo);

        return false;
    }
    return true;
}

/** Appends/overwrites URL search params e.g. from location.search.
 * Used to change few URL parameters while preserving existing ones.
 * Example call: updateUrlParams(new URLSearchParams(window.location.search), { "entry": "problem" })
 */
export function updateUrlParams(params: URLSearchParams, overwriteParams: { [name: string]: string | null }): void {
    Object.entries(overwriteParams).forEach(([key, value]) => value == null ? params.delete(key) : params.set(key, value));
}

// Gets specified url with only the specified set of params
export const restrictUrlParams = (url: string, allowedParams: string[]): string => {
    const location = new URL(url);

    let search: string = location.search;
    if (!hasItems(allowedParams)) {
        search = "";
    }

    if (search && hasItems(allowedParams)) {
        const urlParams = new URLSearchParams(search);
        const keys = Array.from(urlParams.keys());

        for (const key of keys) {
            if (allowedParams.every(x => x !== key)) {
                urlParams.delete(key);
            }
        }
        const urlParamsStr = urlParams.toString();
        search = urlParamsStr ? `?${urlParamsStr}` : "";
    }

    return `${location.origin}${location.pathname}${search}${location.hash}`;
}

/** returns the first path for a given URL pathname. Used to retrieve space slugs to show correct views and theme colors 
 * for various parts of the UI.
 * Note that valid strings e.g. "tomato" may not be valid slugs (as in, don't exist in the DB or not a valid space). Thus 
 * the "potential" in the function name.
*/
export function getPotentialSlugFromPathName(pathname: string | null): string | null {
    if (pathname === null || pathname === "" || pathname[0] !== "/") {
        return null;
    }
    return pathname.split("/")[1];
}
